(ns app.front.view.property.audit-log
  "Property audit log view."
  (:require [app.front.entities :as entities]
            [app.front.ui.section :as section]
            [app.front.ui.table :as ui-table]
            [app.front.view.attachment.common :as attachment-common]
            [app.front.view.property.subs :as property-subs]
            [app.front.view.property.view :as property-view]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.i18n :as i18n]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

(rf/reg-event-fx ::fetch-audit-events
  (fn [_ [_ property-id]]
    {:dispatch [:comm/command-monitored
                {:command    :property/audit-events
                 :method     :get
                 :data       (util/mmap property-id)
                 :on-success [::entities/merge-entity-db]}]}))

(rf/reg-sub ::events-sorted
  (fn [[_ _ property-id]]
    [(rf/subscribe [::entities/context])
     (rf/subscribe [::entities/entities {:entity/type   :entity.type/audit-event
                                         :entity/parent property-id}])])
  (fn [[ctx events] [_ event-types]]
    (->> events
         (filter #(contains? event-types (:audit-event/type %)))
         (map (fn [event] {:event   event
                           :creator (entities/find-entity ctx (:entity/creator event))}))
         (sort-by (comp :entity/created :event)))))


(rf/reg-sub ::view-data
  :<- [::property-subs/property-href]
  (fn [property-href _]
    {:back-href property-href}))


(rf/reg-sub ::single-language-group?
  (fn [[_ property-id]]
    (rf/subscribe [::entities/entity property-id]))
  (fn [property _]
    (-> property :property/group i18n-model/group-languages count (= 1))))

(def ^{:arglists '([audit-event lang])} attribute-name
  (i18n-model/translation-fallback-fn :audit-event/attribute-names))

(defn event->related [entity-type single? event]
  (let [related (->> event
                     :audit-event/related-entities
                     (filter (eu/is-type-fn entity-type)))]
    (if single?
      (first related)
      related)))

(defn template-type [event]
  (->> event
       (event->related :entity.type/property-attribute-template true)
       :property-attribute-template/type))

(defn event-value [event value-key]
  (-> event value-key vals first))

(defn value-by-lang [lang event value-key]
  (some-> event (event-value value-key) (i18n-model/get-text-from-entities-or-map lang)))

(defn language-specific-value [languages event value-key]
  (when-let [[lang value] (some->> (event-value event value-key)
                                   (mapcat identity))]
    (str (-> languages (get-in [lang :short-name]) str/upper-case) ": " value)))

(defmulti attribute-value (fn [_ value-key event] (-> event :event value-key keys first)))

(defmethod attribute-value :property-attribute/value-localized
  [{:keys [lang languages single-language-group?]} value-key {:keys [event]}]
  (if (or (-> event template-type (= :property-attribute-template.type/select))
          single-language-group?)
    ;; Show value according to lang selection.
    (value-by-lang lang event value-key)
    ;; Show which language-specific value was changed.
    (language-specific-value languages event value-key)))

(defmethod attribute-value :property-attribute/value-date
  [{:keys [format-date]} value-key {:keys [event]}]
  (some-> event (event-value value-key) format-date))

(defn range-date [{:keys [tr format-date]} value-key event title-key]
  (when-let [date-str (some-> event (event-value value-key) format-date)]
    (str (tr [title-key]) ": " date-str)))

(defmethod attribute-value :property-attribute/value-start-date
  [ctx value-key {:keys [event]}]
  (range-date ctx value-key event :audit-log/start-date))

(defmethod attribute-value :property-attribute/value-end-date
  [ctx value-key {:keys [event]}]
  (range-date ctx value-key event :audit-log/end-date))

(defmethod attribute-value :property-attribute/value-boolean
  [{:keys [lang]} value-key {:keys [event]}]
  (value-by-lang lang event value-key))

(defmethod attribute-value :default
  [_ value-key {:keys [event]}]
  (event-value event value-key))

(defn- attribute-events-list [{:keys [tr lang format-date-time] :as ctx}]
  (let [property            (<sub [::property-subs/property])
        property-db-units?  (<sub [::ff/enabled? :feature-flag.id/property-db-units])
        multiple-addresses? (<sub [::ff/enabled? :feature-flag.id/property-multiple-addresses])
        data-integrity?     (<sub [::ff/enabled? :feature-flag.id/property-data-integrity-tooling])
        loading?            (<sub [:comm/command-processing? :property/audit-events])
        event-types         (cond-> #{:audit-event.type/edit-property-attribute
                                      :audit-event.type/remove-property-attribute}
                              property-db-units?
                              (into #{:audit-event.type/edit-property-id
                                      :audit-event.type/edit-property-legal
                                      :audit-event.type/edit-property-address})
                              data-integrity?
                              (conj :audit-event.type/property-gis-identity-mismatch)
                              multiple-addresses?
                              (into #{:audit-event.type/remove-property-address
                                      :audit-event.type/set-primary-property-address}))
        audit-events        (<sub [::events-sorted event-types (:db/id property)])]
    [:<>
     [:h3.mt-6.mb-4 (tr [:audit-log/property-attributes])]
     (cond
       loading?
       [section/section-skeleton]

       (empty? audit-events)
       (tr [:audit-log/no-events])

       :else
       [ui-table/modern-responsive-table
        {:items   audit-events
         :id-func (comp :db/id :event)
         :tr      tr
         :class   "mb-4"
         :cols    [[(tr [:date/date])
                    (fn [{:keys [event]}]
                      (-> event :entity/created format-date-time))]
                   [(tr [:audit-event/type])
                    (fn [{:keys [event]}]
                      (tr [(:audit-event/type event)]))]
                   [(tr [:user/user])
                    (comp (partial user-model/audit-log-user tr) :creator)]
                   [(tr [:audit-log/attribute-name])
                    #(-> % :event (attribute-name lang))]
                   [(tr [:audit-log/old-attribute-value])
                    (partial attribute-value ctx :audit-event/old-attribute-value)]
                   [(tr [:audit-log/new-attribute-value])
                    (partial attribute-value ctx :audit-event/new-attribute-value)]]}])]))

(defn- attachment-events-list [{:keys [tr format-date-time]}]
  (let [property     (<sub [::property-subs/property])
        loading?     (<sub [:comm/command-processing? :property/audit-events])
        audit-events (<sub [::events-sorted
                            #{:audit-event.type/attachment-added
                              :audit-event.type/attachment-deleted}
                            (:db/id property)])]
    [:<>
     [:h3.mt-6.mb-4 (tr [:audit-log/attachments])]
     (cond
       loading?
       [section/section-skeleton]

       (empty? audit-events)
       (tr [:audit-log/no-events])

       :else
       [ui-table/modern-responsive-table
        {:items   audit-events
         :id-func (comp :db/id :event)
         :tr      tr
         :class   "mb-4"
         :cols    [[(tr [:date/date])
                    (fn [{:keys [event]}]
                      (-> event :entity/created format-date-time))]
                   [(tr [:audit-event/type])
                    (fn [{:keys [event]}]
                      (tr [(:audit-event/type event)]))]
                   [(tr [:user/user])
                    (comp (partial user-model/audit-log-user tr) :creator)]
                   [(tr [:audit-log/attachment])
                    (fn [{:keys [event]}]
                      (let [{:attachment/keys [contents]} (event->related :entity.type/attachment true event)
                            types                         (event->related :entity.type/attachment-type false event)]
                        [:div.flex.flex-col
                         [:div.font-bold (attachment-common/attachment-types-text types)]
                         (when contents [:div.mt-2 contents])]))]]}])]))

(defn audit-log-section [{:keys [tr] :as ctx}]
  [section/section
   "audit-log-section"
   {:tr tr}
   :notifications/audit-log
   [:div
    [attribute-events-list ctx]
    [attachment-events-list ctx]]])

(defn view [match]
  (let [tr                     (<sub [::property-subs/tr])
        format-date-time       (<sub [::date-util/date-time-fn])
        format-date            (<sub [::date-util/date-fn])
        lang                   (<sub [:language])
        languages              (<sub [::i18n/locales])
        single-language-group? (<sub [::single-language-group? (-> match :parameters :path :db-id)])]
    [:div.my-5.mx-auto.cloudpermit-page
     [audit-log-section (util/mmap tr format-date-time format-date lang languages single-language-group?)]]))

(def route
  {:name        :view/property-audit-log
   :view        view
   :breadcrumb  property-view/breadcrumb
   :view-sub    [::view-data]
   :parameters  {:path {:db-id s/Int}}
   :controllers [{:parameters {:path [:db-id]}
                  :start      (fn [{{:keys [db-id]} :path}]
                                (>evt [::property-subs/get-property-data db-id])
                                (>evt [::fetch-audit-events db-id]))}]})
